import React, { ChangeEventHandler, DragEventHandler, useEffect, useState } from "react";

import { getArrowIcon } from "../services/icons";
import { validateFileSize, validateImageType } from "../services/utils";
import ErrorFormMessage from "./ErrorFormMessage";

type PropsType = {
  multiple?: boolean;
  files: FileType[];
  onChangeHandler: (file: FileList) => void;
  err?: string | boolean;
};

type FileType = {
  preview: string;
  data?: File;
};

export default function ImageUpload({ multiple = false, onChangeHandler, files, err }: PropsType) {
  const [error, setError] = useState(false as string | boolean);

  useEffect(() => {
    setError(err);
  }, [err]);

  const processFiles = (files: FileList) => {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (!validateImageType(file.type)) {
        setError("Current file type is not supported");
        return;
      }

      if (!validateFileSize(file.size)) {
        setError("Current file size is more than 2mb");
        return;
      }
    }

    onChangeHandler(files);
  };

  const handleOnDrop: DragEventHandler<HTMLDivElement> = (e) => {
    setError(false);
    if (!e.dataTransfer.files) {
      return;
    }

    const files = e.dataTransfer.files;
    if (!files) {
      return;
    }

    processFiles(files);
  };

  const handleOnChange: ChangeEventHandler<HTMLInputElement> = (e) => {
    setError(false);
    if (!e.target.files) {
      return;
    }

    const files = e.target.files;
    if (!files) {
      return;
    }

    processFiles(files);
  };

  return (
    <div className="flex max-w-full flex-row flex-wrap gap-6">
      {files.map((file) => {
        if (file.preview) {
          return (
            <img key={file.preview} src={file.preview} className="h-[200px] max-h-full w-auto" />
          );
        }
      })}
      <div
        className={`relative flex h-[200px] w-[200px] max-w-[200px] flex-shrink-0 flex-grow items-center justify-center border-[1px] bg-white transition hover:bg-gray-50 ${
          error ? "border-red-500 text-red-500" : "border-[#DCD7D0] text-gray-500"
        }`}
        onDrop={handleOnDrop}
      >
        <input
          type="file"
          className="absolute inset-0 z-50 h-full w-full cursor-pointer opacity-0"
          onChange={handleOnChange}
          accept="image/png, image/jpeg"
          multiple={multiple}
        />
        <div className="flex h-40 w-full flex-col items-center justify-center">
          <div className="flex flex-col items-center justify-center gap-2">
            {getArrowIcon(error ? "#EF4444" : "#878787")}
            <p className="mb-2 text-sm">
              Drag you image here
              <br />
              or click to upload it
            </p>
          </div>
        </div>
      </div>
      {error ? <ErrorFormMessage message={error} /> : null}
    </div>
  );
}
